import React, { useEffect, useContext, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import IdentifiedOrganismsBox from './IdentifiedSpecies/IdentifiedOrganismsBox';
import DetailedRunMetadataSummary from './Runs/DetailedRunMetadataSummary';
import DetailedSampleMetadataSummary from './Samples/DetailedSampleMetadataSummary';
import DetailedSequencingYieldSummary from './Samples/DetailedSequencingYieldSummary';
import DetailedSequencingQualitySummary from './Samples/DetailedSequencingQualitySummary';
import DetailedAssemblyQualitySummary from './Samples/DetailedAssemblyQualitySummary';
import BackButton from './Buttons/BackButton';
import IdentifiedSpeciesFiltersDialog from './Samples/IdentifiedSpeciesFiltersDialog';
import { IdentifiedSpeciesFiltersDispatchContext } from './context/IdentifiedSpeciesFiltersContext';
import { sortByIdConfidence } from './IdentifiedSpecies/utilities';
import { useDataset, useSample, useActiveAnalysis, useIdentifiedSpecies, useAmrPredictions, useResistanceGenes, useImputations } from './services/api/KeynomeAPIHooks';
import { useRecordSampleViewed } from './services/api/KeynomeAPIMutations';

// If a sample is an isolate, by default we only display
// organisms for which >= 10% of the genome was recovered
// (CID value >= 0.1)
const ISOLATE_CID_INITIAL_DISPLAY_THRESHOLD = 0.1;

// If a sample is an isolate, we expect one organism
// to have been recovered with >=80% breadth
// (CID value >= 0.8)
const ISOLATE_CID_EXPECTED_POSITIVE_THRESHOLD = 0.8;

function Sample() {
  const params = useParams();
  
  const { activeProject, organization, hasAccessReportViruses } = useOutletContext();
  const { setVisibleSpeciesCt, setLoadingResistanceGenes, setLoadingImputations } = useContext(IdentifiedSpeciesFiltersDispatchContext);
  const lastRecordedSampleUuidRef = useRef(null);

  const { data: run, isLoading: runLoading } = useDataset(params.runId);
  const { data: sample, isLoading: sampleLoading } = useSample(params.sampleId);
  const { mutateAsync: recordSampleViewed } = useRecordSampleViewed();
  const { data: activeAnalysis, isLoading: analysisLoading } = useActiveAnalysis(
    params.sampleId,
    { queryParams: { extraFields: ['status', 'positive_species_calls_ct', 'analyzed_with_allow_low_quality'] } }
  );
  const { data: identifiedSpecies, isLoading: identifiedSpeciesLoading } = useIdentifiedSpecies(activeAnalysis?.id);

  const accessibleIdentifiedSpecies = sortByIdConfidence(
    identifiedSpecies?.filter(item => hasAccessReportViruses || !item.attributes.is_virus)
  )
  const positiveIdentifiedSpecies = accessibleIdentifiedSpecies
    ?.filter(item => item.attributes.species_call_positive)

  // Fetch resistance genes and imputations for sample to manage loading state
  const { data: amrPredictions, isLoading: predicationLoading } = useAmrPredictions(positiveIdentifiedSpecies.map(species => species.id));
  const { isLoading: resistanceGenesLoading } = useResistanceGenes(positiveIdentifiedSpecies.map(species => species.id));
  const { isLoading: imputationsLoading } = useImputations(amrPredictions?.map(prediction => prediction?.id));

  const dataLoading = runLoading || sampleLoading || analysisLoading || identifiedSpeciesLoading || predicationLoading;
  useEffect(() => {
    const sampleUuid = sample?.attributes?.uuid;
    if (!sampleUuid) return;
    if (lastRecordedSampleUuidRef.current === sampleUuid) return;

    lastRecordedSampleUuidRef.current = sampleUuid;

    recordSampleViewed({ sampleId: sampleUuid }).catch((error) => {
      console.error(error);
    });
  }, [sample?.attributes?.uuid, recordSampleViewed]);

  useEffect(() => {
    // Set loading states for resistance genes and imputations (disable/enable toggles in IdentifiedSpeciesFiltersDialog)
    setLoadingResistanceGenes(dataLoading || resistanceGenesLoading);
    setLoadingImputations(dataLoading || imputationsLoading);
  }, [dataLoading, resistanceGenesLoading, imputationsLoading, setLoadingResistanceGenes, setLoadingImputations]);

  const isIsolate = sample?.attributes?.sample_type === 'isolate';
  const isIllumina = run?.attributes?.sequencing_platform === 'illumina' || run?.attributes?.platform === 'illumina';

  const microbialMegabases = accessibleIdentifiedSpecies
    .reduce((sum, species) => sum + (species.attributes.reads_megabases_to_species ?? 0), 0);
  const microbialReads = accessibleIdentifiedSpecies
    .reduce((sum, species) => sum + (species.attributes.reads_to_species ?? 0), 0);
  const positiveIdentifiedSpeciesCt = accessibleIdentifiedSpecies
    .filter(item => item.attributes.species_call_positive)
    .length;

  useEffect(() => {
    if (isIsolate) {
      const initialVisibleIdentifiedOrganismsCt = accessibleIdentifiedSpecies
        .filter(item => item.attributes.species_call_raw_value >= ISOLATE_CID_INITIAL_DISPLAY_THRESHOLD)
        .length
      setVisibleSpeciesCt(Math.min(initialVisibleIdentifiedOrganismsCt, 5));
    }
    else {
      const kidPositiveSpeciesCt = accessibleIdentifiedSpecies
        .filter(item => item.attributes.species_call_positive)
        .length;
        setVisibleSpeciesCt(Math.min(accessibleIdentifiedSpecies.length, Math.max(kidPositiveSpeciesCt, 5)));
    }
  }, [isIsolate, isIllumina, accessibleIdentifiedSpecies, microbialMegabases, setVisibleSpeciesCt]);

  const isolateWarning = useMemo(() => {
    if (!isIsolate || accessibleIdentifiedSpecies.length === 0) return null;

    const topIdentifiedOrganism = accessibleIdentifiedSpecies[0];
    if (!topIdentifiedOrganism.attributes) return null;

    const topIdentifiedOrganismHasSufficientRecovery = topIdentifiedOrganism.attributes.species_call_raw_value >= ISOLATE_CID_EXPECTED_POSITIVE_THRESHOLD;

    if (!topIdentifiedOrganismHasSufficientRecovery) {
      return (
        <Grid item xs={12}>
          <Alert severity="error">
            This sample was reported as an isolate, but {"<"}80% of the genome was recovered for the top-ranked organism. Consider assessing sample preparation or edit the sample type in the SAMPLE / ANALYSIS section.
          </Alert>
        </Grid>
      );
    }

    if (accessibleIdentifiedSpecies.length === 1) return null;

    const secondIdentifiedOrganism = accessibleIdentifiedSpecies[1];
    const secondIdentifiedOrganismIsContaminant = secondIdentifiedOrganism.attributes.species_call_raw_value >= ISOLATE_CID_INITIAL_DISPLAY_THRESHOLD;
    if (!secondIdentifiedOrganismIsContaminant) {
      return null;
    }

    const topIdentifiedOrganismGenus = topIdentifiedOrganism.attributes.species.split(' ')[0];
    const secondIdentifiedOrganismGenus = secondIdentifiedOrganism.attributes.species.split(' ')[0];

    const topTwoOrganismsSameGenus = topIdentifiedOrganismGenus === secondIdentifiedOrganismGenus;

    if (topTwoOrganismsSameGenus) {
      return (
        <Grid item xs={12}>
          <Alert severity="error">
            The taxonomic database for this strain may not be well defined, leading to signal splitting among closely related species.
          </Alert>
        </Grid>
      );
    }
    
    return (
      <Grid item xs={12}>
        <Alert severity="error">
          <b>Potential contamination detected.</b> This sample was reported as an isolate but appears to contain multiple organisms. If this sample is not an isolate, edit the sample type in the SAMPLE / ANALYSIS section.
        </Alert>
      </Grid>
    );
  }, [accessibleIdentifiedSpecies, isIsolate]);

  // Get the URL for the sample's dataset, to use for the back button
  const datasetURL = run ? new URL(run.attributes.keynome_web_url) : null;
  const analyzedWithAllowLowQuality = activeAnalysis?.attributes?.analyzed_with_allow_low_quality === true;

  return (
    <Box>
      <Grid container sx={{ borderBottom: '#bdbdbd 2px solid' }}>
        <Grid item xs={12}>
          <Box>
            <Grid container maxWidth="lg" sx={{ m: 'auto', p: '10px' }}>
              <Grid item xs={2} sx={{ borderRight: '#bdbdbd 2px solid' }}>
                <Box>
                  <BackButton backToLink={datasetURL?.pathname} backButtonText="ALL SAMPLES" />
                </Box>
              </Grid>
              <Grid item xs={4} align="left" sx={{ pt: 0.5, pl: 2 }}>
                Pathosight RUO Results
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Grid container maxWidth="lg" sx={{ m: 'auto', mb: 2, paddingRight: '30px' }} spacing={2} align="auto">
          <Grid item xs={12}>
              <Alert severity="warning"><b>For Research Use Only.</b> Not for use in diagnostic procedures.</Alert>
          </Grid>
          {analyzedWithAllowLowQuality && (
            <Grid item xs={12}>
                <Alert severity="warning">
                  <b>Low-quality data included.</b> This sample was analyzed with the allow low quality flag enabled; results may be impacted.
                </Alert>
            </Grid>
          )}
          {isolateWarning}
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedRunMetadataSummary run={run} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedSampleMetadataSummary
              sample={sample}
              analysis={activeAnalysis}
              activeProject={activeProject}
              positiveIdentifiedSpeciesCt={positiveIdentifiedSpeciesCt}
              runId={params.runId}
            />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Box>
            <IdentifiedOrganismsBox
              isDiagnosticView={false}
              identifiedSpecies={accessibleIdentifiedSpecies}
              isIllumina={isIllumina}
              run={run}
              sample={sample}
              analysis={activeAnalysis}
              totalMicrobialMegabases={microbialMegabases}
              organization={organization}
            />
          </Box>
        </Grid>
        <IdentifiedSpeciesFiltersDialog 
            identifiedSpecies={accessibleIdentifiedSpecies}
        />
            <>
        <Grid item xs={12}>
          <Paper elevation={2}>
            <DetailedSequencingYieldSummary
              analysis={activeAnalysis}
              microbialMegabases={microbialMegabases}
              microbialReads={microbialReads}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedSequencingQualitySummary analysis={activeAnalysis} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={2}>
            <DetailedAssemblyQualitySummary analysis={activeAnalysis} 
            isIllumina={isIllumina}
            />
          </Paper>
        </Grid>
            </>
      </Grid>
    </Box>
  );
}

export default Sample;
